import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2 } from "lucide-react";

const HERO = "https://images.pexels.com/photos/13375852/pexels-photo-13375852.jpeg";

export default function BookConsultation() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    apartment_type: "3BHK", apartment_size: "",
    budget: "₹15L - ₹25L", preferred_date: "", message: ""
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    let floor_plan_path = null;
    try {
      if (file) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", "floorplans");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        floor_plan_path = data.path;
        setUploading(false);
      }
      await api.post("/leads", {
        ...form,
        apartment_size: form.apartment_size ? Number(form.apartment_size) : null,
        floor_plan_path,
        source: "Website Consultation",
      });
      setDone(true);
      toast.success("Consultation request received. We'll call you within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false); setUploading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="book-success" className="px-6 md:px-12 lg:px-24 py-32 text-center max-w-2xl mx-auto">
        <CheckCircle2 size={48} className="text-[#D4AF37] mx-auto" strokeWidth={1.5} />
        <h1 className="font-heading text-5xl mt-6">Thank you, {form.name.split(" ")[0]}.</h1>
        <p className="text-[#5C5C5C] mt-4 leading-relaxed">
          Your consultation request is in. Our senior designer will call you within 24 hours on <strong>{form.phone}</strong> to confirm a slot.
        </p>
        <a href="/" className="btn-secondary mt-10 inline-block">Back to home</a>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="grid grid-cols-1 lg:grid-cols-12 min-h-[80vh]">
      <div className="lg:col-span-5 relative bg-[#F5F3EC] overflow-hidden">
        <img src={HERO} alt="Designer with color swatches" className="absolute inset-0 w-full h-full object-cover opacity-95" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent to-transparent" />
        <div className="relative h-full p-10 md:p-14 flex flex-col justify-end text-white">
          <p className="overline text-[#D4AF37]">Book a Consultation</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-[-0.02em] mt-4 leading-[1.02]">
            45 minutes that change<br /> <em className="italic text-[#E8D9B3]">how your home feels.</em>
          </h1>
          <p className="mt-6 max-w-md text-white/80 leading-relaxed">
            We meet at our Kithiganur office or on a video call. Bring your floor plan, your Pinterest board, and your honest budget.
          </p>
        </div>
      </div>

      <div className="lg:col-span-7 px-6 md:px-12 lg:px-20 py-16 md:py-20">
        <form onSubmit={submit} className="max-w-2xl space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Your name</label>
              <input data-testid="book-name" required value={form.name} onChange={onChange("name")} className="input-line mt-2" placeholder="Full name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Phone</label>
              <input data-testid="book-phone" required value={form.phone} onChange={onChange("phone")} className="input-line mt-2" placeholder="+91 9XXXX XXXXX" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Email</label>
            <input data-testid="book-email" required type="email" value={form.email} onChange={onChange("email")} className="input-line mt-2" placeholder="you@email.com" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Apartment</label>
              <select data-testid="book-apartment" value={form.apartment_type} onChange={onChange("apartment_type")} className="input-line mt-2">
                {["1BHK", "2BHK", "3BHK", "4BHK", "Villa"].map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Size (sqft)</label>
              <input data-testid="book-size" type="number" value={form.apartment_size} onChange={onChange("apartment_size")} className="input-line mt-2" placeholder="1200" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Budget</label>
              <select data-testid="book-budget" value={form.budget} onChange={onChange("budget")} className="input-line mt-2">
                {["₹10L - ₹15L", "₹15L - ₹25L", "₹25L - ₹40L", "₹40L - ₹50L+"].map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Preferred date</label>
            <input data-testid="book-date" type="date" value={form.preferred_date} onChange={onChange("preferred_date")} className="input-line mt-2" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Tell us about your home</label>
            <textarea data-testid="book-message" value={form.message} onChange={onChange("message")} rows={3} className="input-line mt-2 resize-none" placeholder="Style references, must-haves, must-nots…" />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">Floor plan (optional · PDF/JPG/PNG · max 10MB)</label>
            <label htmlFor="floor-upload" className="mt-3 border border-dashed border-[#1A1A1A]/30 hover:border-[#1A1A1A] cursor-pointer flex items-center gap-3 p-5 transition-colors">
              <Upload size={18} className="text-[#B38F24]" />
              <span className="text-sm">{file ? file.name : "Click or drop a floor plan here"}</span>
              <input id="floor-upload" data-testid="book-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            {uploading && <p className="text-xs text-[#B38F24] mt-2">Uploading…</p>}
          </div>

          <button data-testid="book-submit" disabled={submitting} className="btn-primary mt-2">
            {submitting ? "Submitting…" : "Request Consultation"}
          </button>
          <p className="text-xs text-[#5C5C5C]">By submitting, you agree to be contacted by Dreamy Interiors regarding your project.</p>
        </form>
      </div>
    </div>
  );
}
